import { useCallback } from 'react';

import { GenericProvider } from '@/features/game-context/types';
import { handleUnknownMapping } from '@/features/game-window-integration/helpers/generic-helpers';
import { PROVIDER_ADAPTORS } from '@/features/game-window-integration/providers/generic-provider/payload-adaptors';
import {
  GenericErrorEvent,
  GenericEvents,
  GenericEventsData,
} from '@/features/game-window-integration/providers/generic-provider/types.ts';
import { remapEvent } from '@/features/game-window-integration/providers/generic-provider/utils.ts';
import { ProviderLoggerMessages } from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const useGenericProviderHandlers = (
  provider: GenericProvider,
): {
  handleGameReady: () => void;
  handleGameLoadStarted: () => void;
  handleGameLoadProgress: (data) => void;
  handleSpinInProgress: (data: GenericEventsData) => void;
  handleAutoplayStop: () => void;
  handleCloseGame: () => void;
  handleResetGame: () => void;
  handleNotImplemented: () => void;
  handleVolumeChange: (data: GenericEventsData) => void;
  handlePayloadVolumeChange: (data: GenericEventsData) => void;
  handleNotification: (data: GenericEventsData) => void;
  getGameProviderFrame: () => Window | null | undefined;
  handleError: (payload: GenericEventsData) => void;
} => {
  const getGameProviderFrame = useCallback(
    () =>
      document.querySelector<HTMLIFrameElement>('#game-client')?.contentWindow,
    [],
  );

  const handleGameLoadStarted = useCallback(() => {
    WrapperProxy.setGameLoadStarted();
  }, []);

  const handleGameLoadProgress = useCallback((data) => {
    WrapperProxy.setGameLoadProgress(data?.payload?.progress);
    if (data?.payload?.progress === 100) {
      WrapperProxy.setGameLoaded();
    }
  }, []);

  const handleGameReady = useCallback(() => {
    setTimeout(() => {
      WrapperProxy.setGameLoaded();
    }, 1000);
  }, []);

  const handleVolumeChange = useCallback(
    (data: GenericEventsData) => {
      const { type } = data ?? {};

      if (!type) return;
      WrapperProxy.setAudio(
        type === remapEvent(provider, GenericEvents.SOUND_ON),
      );
    },
    [provider],
  );

  const handlePayloadVolumeChange = useCallback((data: GenericEventsData) => {
    type VolumePayload = { mute: boolean; volume?: number | null };
    type VolumeData = { payload: VolumePayload };

    const {
      type,
      data: dataField,
      payload,
    } = (data ?? {}) as GenericEventsData<VolumePayload | VolumeData>;

    const muteValue =
      (payload as VolumePayload | undefined)?.mute ??
      (dataField as VolumeData | undefined)?.payload?.mute;

    if (!type || muteValue === undefined) return;

    WrapperProxy.setAudio(!muteValue);
  }, []);

  const handleNotification = useCallback(
    (data: GenericEventsData) => {
      const { type, stateChangeMessageType, wrapperMessageType } = data ?? {};
      if (!type) return;
      if (
        (type === remapEvent(provider, GenericEvents.NOTIFICATION) ||
          type === remapEvent(provider, GenericEvents.NOTIFICATION_HANDLED)) &&
        (stateChangeMessageType || wrapperMessageType)
      ) {
        switch (stateChangeMessageType ?? wrapperMessageType) {
          case remapEvent(provider, GenericEvents.SOUND_ON):
            WrapperProxy.setAudio(true);
            break;
          case remapEvent(provider, GenericEvents.SOUND_OFF):
            WrapperProxy.setAudio(false);
            break;
          case remapEvent(provider, GenericEvents.DISMISS_ERROR):
            WrapperProxy.setSpinInProgress(false);
            break;
        }
      }
    },
    [provider],
  );

  const handleSpinInProgress = useCallback(
    (data: GenericEventsData) => {
      const { type } = data ?? {};

      if (!type) return;
      WrapperProxy.setSpinInProgress(
        type === remapEvent(provider, GenericEvents.SPIN_IN_PROGRESS),
      );
    },
    [provider],
  );

  const handleAutoplayStop = useCallback(() => {
    WrapperProxy.setSpinInProgress(false);
  }, []);

  const handleCloseGame = useCallback(() => {
    WrapperProxy.setGameLauncherClosed();
  }, []);

  const handleResetGame = useCallback(() => {
    WrapperProxy.resetGame();
  }, []);

  const handleNotImplemented = useCallback(() => {
    Logger.rumAction({
      name: ProviderLoggerMessages.NOT_IMPLEMENTED,
      useCase: DdEventCategory.ProviderIntegration,
      eventType: DdEventType.Info,
      context: {
        provider,
      },
    });
  }, [provider]);

  const handleError = useCallback(
    (payload: GenericEventsData) => {
      if (!payload) return;
      const errorPayload = payload.payload as GenericErrorEvent;
      const error = PROVIDER_ADAPTORS[provider]?.processError(errorPayload);

      if (!error) {
        Logger.rumAction({
          name: ProviderLoggerMessages.ERROR_NOT_HANDLED,
          useCase: DdEventCategory.ProviderIntegration,
          eventType: DdEventType.Info,
          context: {
            provider,
          },
        });
        handleUnknownMapping(payload, payload.errorInfo);
        return;
      }
      const { errorType, errorCode, errorMessage } = error;
      WrapperProxy.setProviderError({
        display: true,
        errorCode,
        errorMessage,
        errorType,
      });
    },
    [provider],
  );

  return {
    handleGameLoadStarted,
    handleGameLoadProgress,
    handleGameReady,
    handleSpinInProgress,
    handleAutoplayStop,
    handleNotImplemented,
    handleVolumeChange,
    handlePayloadVolumeChange,
    handleNotification,
    handleCloseGame,
    handleResetGame,
    handleError,
    getGameProviderFrame,
  };
};
